import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiX, FiEdit2, FiTrash2, FiImage } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { api } from '../../../../api/api';
import StatusConfirmationModal from '../../../components/StatusConfirmationModal';

const fetchTaxonomyData = async () => {
  try {
    const [categoriesResponse, brandsResponse] = await Promise.all([
      api.getCategories(),
      api.getBrands()
    ]);

    // Ensure we have arrays of data
    const categories = Array.isArray(categoriesResponse) 
      ? categoriesResponse 
      : (categoriesResponse?.results || []);
    
    const brands = Array.isArray(brandsResponse) 
      ? brandsResponse 
      : (brandsResponse?.results || []);

    return { 
      brands: brands.filter(brand => brand.is_active), 
      categories: categories.filter(category => category.is_active)
    };
  } catch (error) {
    toast.error('Failed to load brands and categories');
    return { brands: [], categories: [] };
  }
};

// Add Modal Component
const AddCarouselModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    link_type: 'brand',
    brand: '',
    category: '',
    order: 0,
    image: null,
    is_active: true
  });

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const { brands: brandsData, categories: categoriesData } = await fetchTaxonomyData();
        setBrands(brandsData);
        setCategories(categoriesData);
        
        // Set default values if available
        if (brandsData.length > 0 && formData.link_type === 'brand') {
          setFormData(prev => ({ ...prev, brand: brandsData[0].id.toString() }));
        }
        if (categoriesData.length > 0 && formData.link_type === 'category') {
          setFormData(prev => ({ ...prev, category: categoriesData[0].id.toString() }));
        }
      } catch (error) {
        toast.error('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.link_type || !formData.order || !formData.image) {
      toast.error('Please fill in all required fields');
      return;
    }

    const data = new FormData();
    data.append('title', formData.title);
    data.append('link_type', formData.link_type);
    data.append('order', formData.order);
    data.append('is_active', formData.is_active);
    
    if (formData.link_type === 'brand' && formData.brand) {
      data.append('brand', formData.brand);
    } else if (formData.link_type === 'category' && formData.category) {
      data.append('category', formData.category);
    }
    
    if (formData.image) {
      data.append('image', formData.image);
    }

    await onSave(data);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full">
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Add New Carousel</h3>
          <button onClick={onClose} className="p-2">
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title*</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full border rounded-lg p-2"
              placeholder="Enter carousel title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Link Type*</label>
            <select
              required
              value={formData.link_type}
              onChange={(e) => {
                const newLinkType = e.target.value;
                setFormData(prev => ({
                  ...prev,
                  link_type: newLinkType,
                  brand: newLinkType === 'brand' ? (brands[0]?.id.toString() || '') : '',
                  category: newLinkType === 'category' ? (categories[0]?.id.toString() || '') : ''
                }));
              }}
              className="w-full border rounded-lg p-2"
            >
              <option value="brand">Brand</option>
              <option value="category">Category</option>
            </select>
          </div>
          {formData.link_type === 'brand' ? (
            <div>
              <label className="block text-sm font-medium mb-1">Brand*</label>
              <select
                required
                value={formData.brand}
                onChange={(e) => {
                  const selectedBrand = brands.find(b => b.id === e.target.value);
                  setFormData(prev => ({ 
                    ...prev, 
                    brand: e.target.value,
                    brand_name: selectedBrand?.name // Store brand name along with ID
                  }));
                }}
                className="w-full border rounded-lg p-2"
              >
                <option value="">Select a brand</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Category*</label>
              <select
                required
                value={formData.category}
                onChange={(e) => {
                  const selectedCategory = categories.find(c => c.id === e.target.value);
                  setFormData(prev => ({ 
                    ...prev, 
                    category: e.target.value,
                    category_name: selectedCategory?.name // Store category name along with ID
                  }));
                }}
                className="w-full border rounded-lg p-2"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Order*</label>
            <input
              type="number"
              required
              value={formData.order}
              onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))}
              className="w-full border rounded-lg p-2"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Image*</label>
            <input
              type="file"
              required
              accept="image/*"
              onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.files[0] }))}
              className="w-full border rounded-lg p-2"
            />
          </div>
          <div className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-600">Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Add Carousel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditCarouselModal = ({ carousel, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: carousel.title || '',
    link_type: carousel.link_type || 'brand',
    brand: carousel.brand?.id?.toString() || '',
    category: carousel.category?.id?.toString() || '',
    order: carousel.order || 0,
    image: null,
    is_active: carousel.is_active
  });

  const validateImage = (file) => {
    // Max size: 5MB
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Image size should be less than 5MB');
      return false;
    }

    // Allowed types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, GIF and SVG images are allowed');
      return false;
    }

    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && validateImage(file)) {
      setFormData(prev => ({ ...prev, image: file }));
    }
  };

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const { brands: brandsData, categories: categoriesData } = await fetchTaxonomyData();
        setBrands(brandsData);
        setCategories(categoriesData);
      } catch (error) {
        toast.error('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.link_type || !formData.order) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Create JSON data for non-file fields
    const jsonData = {
      title: formData.title,
      link_type: formData.link_type,
      order: parseInt(formData.order),
      is_active: formData.is_active
    };

    // Add brand or category based on link_type
    if (formData.link_type === 'brand' && formData.brand) {
      jsonData.brand = parseInt(formData.brand);
    } else if (formData.link_type === 'category' && formData.category) {
      jsonData.category = parseInt(formData.category);
    }

    // If there's a new image, use FormData
    if (formData.image instanceof File) {
      const data = new FormData();
      // Append the JSON data as a string
      data.append('data', JSON.stringify(jsonData));
      data.append('image', formData.image);
      await onSave(carousel.id, data, true); // true indicates multipart
    } else {
      // If no new image, send JSON directly
      await onSave(carousel.id, jsonData, false); // false indicates JSON
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full">
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Edit Carousel</h3>
          <button onClick={onClose} className="p-2">
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title*</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full border rounded-lg p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Link Type*</label>
            <select
              required
              value={formData.link_type}
              onChange={(e) => setFormData(prev => ({ ...prev, link_type: e.target.value }))}
              className="w-full border rounded-lg p-2"
            >
              <option value="brand">Brand</option>
              <option value="category">Category</option>
            </select>
          </div>
          {formData.link_type === 'brand' ? (
            <div>
              <label className="block text-sm font-medium mb-1">Brand*</label>
              <select
                required
                value={formData.brand}
                onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                className="w-full border rounded-lg p-2"
              >
                <option value="">Select a brand</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-1">Category*</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full border rounded-lg p-2"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Order*</label>
            <input
              type="number"
              required
              value={formData.order}
              onChange={(e) => setFormData(prev => ({ ...prev, order: e.target.value }))}
              className="w-full border rounded-lg p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full border rounded-lg p-2"
            />
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CarouselCard = ({ carousel, onEdit, onDelete }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="aspect-[16/9] overflow-hidden bg-gray-100">
        {!imageError ? (
          <img
            src={carousel.image}
            alt={carousel.title}
            className="w-full h-full object-cover"
            onError={() => {
              console.log('Failed to load image:', carousel.image);
              setImageError(true);
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-gray-400 flex flex-col items-center">
              <FiImage className="w-8 h-8 mb-2" />
              <span className="text-sm">Image not available</span>
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <h4 className="font-semibold mb-2">{carousel.title}</h4>
        <div className="text-sm text-gray-600 mb-2">
          <p>Type: {carousel.link_type}</p>
          <p>
            Linked to: {carousel.link_type === 'brand' 
              ? carousel.brand?.name 
              : carousel.category?.name}
          </p>
          <p>Order: {carousel.order}</p>
        </div>
        <div className="flex justify-between items-center">
          <span className={`px-2 py-1 rounded-full text-xs ${
            carousel.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {carousel.is_active ? 'Active' : 'Inactive'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(carousel)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
            >
              <FiEdit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(carousel)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-full"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CarouselTab = () => {
  const [carousels, setCarousels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCarousel, setEditingCarousel] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [carouselToDelete, setCarouselToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchCarousels();
  }, []);

  const fetchCarousels = async () => {
    try {
      setIsLoading(true);
      const data = await api.getCarousels();
      console.log('Carousel API Response:', data);
      // Add image URL debugging

      setCarousels(data);
    } catch (err) {
      setError('Failed to fetch carousels');
      toast.error('Failed to load carousels');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCarousel = (carousel) => {
    setCarouselToDelete(carousel);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await api.deleteCarousel(carouselToDelete.id);
      setCarousels(prev => prev.filter(carousel => carousel.id !== carouselToDelete.id));
      toast.success('Carousel deleted successfully');
    } catch (error) {
      toast.error('Failed to delete carousel');
    } finally {
      setShowDeleteModal(false);
      setCarouselToDelete(null);
    }
  };

  const handleEditCarousel = async (formData) => {
    try {
      await api.updateCarousel(editingCarousel.id, formData);
      await fetchCarousels(); // Refresh the list
      setEditingCarousel(null);
      toast.success('Carousel updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update carousel');
    }
  };

  const handleAddCarousel = async (formData) => {
    try {
      await api.createCarousel(formData);
      await fetchCarousels(); // Refresh the list
      setShowAddModal(false);
      toast.success('Carousel added successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to add carousel');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold">Carousels</h3>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
          >
            <FiPlus className="h-5 w-5" />
            Add Carousel
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {carousels.map(carousel => (
            <CarouselCard 
              key={carousel.id} 
              carousel={carousel}
              onEdit={() => setEditingCarousel(carousel)}
              onDelete={() => handleDeleteCarousel(carousel)}
            />
          ))}
        </div>
      </div>

      {editingCarousel && (
        <EditCarouselModal
          carousel={editingCarousel}
          onClose={() => setEditingCarousel(null)}
          onSave={handleEditCarousel}
        />
      )}

      {showAddModal && (
        <AddCarouselModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCarousel}
        />
      )}

      <StatusConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCarouselToDelete(null);
        }}
        onConfirm={confirmDelete}
        itemName="Carousel"
        currentStatus={true}
      />
    </div>
  );
};

export default CarouselTab;

























